import React from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { getImageUrl } from '../../../utils/imageUtils';

const DEFAULT_VOLUME = 0.8;

const CoverSection = ({ desktopCoverUrl, mobileCoverUrl, coverVideoUrl }) => {
    const coverVideoRef = React.useRef(null);
    const coverWrapRef = React.useRef(null);
    const [coverMuted, setCoverMuted] = React.useState(true);
    const [shouldLoadCoverVideo, setShouldLoadCoverVideo] = React.useState(false);
    const [coverVideoReady, setCoverVideoReady] = React.useState(false);
    const [coverVolume, setCoverVolume] = React.useState(() => {
        try {
            const raw = localStorage.getItem('profileCoverVideoVolume');
            const v = raw == null ? null : Number(raw);
            if (v == null || !Number.isFinite(v)) return DEFAULT_VOLUME;
            return Math.min(1, Math.max(0, v));
        } catch (_) {
            return DEFAULT_VOLUME;
        }
    });

    React.useEffect(() => {
        const el = coverVideoRef.current;
        if (!el) return;
        try {
            el.volume = Math.min(1, Math.max(0, Number(coverVolume)));
            el.muted = !!coverMuted;
        } catch (_) {
            /* noop */
        }
    }, [coverMuted, coverVolume]);

    React.useEffect(() => {
        try {
            localStorage.setItem('profileCoverVideoVolume', String(coverVolume));
        } catch (_) {
            /* noop */
        }
    }, [coverVolume]);

    React.useEffect(() => {
        setCoverMuted(true);
    }, [coverVideoUrl]);

    React.useEffect(() => {
        setShouldLoadCoverVideo(false);
        setCoverVideoReady(false);

        const url = coverVideoUrl;
        if (!url) return;

        const el = coverWrapRef.current;
        if (!el) return;

        let cancelled = false;

        if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
            const t = setTimeout(() => {
                if (cancelled) return;
                setShouldLoadCoverVideo(true);
            }, 250);
            return () => {
                cancelled = true;
                clearTimeout(t);
            };
        }

        const obs = new IntersectionObserver(
            (entries) => {
                const entry = entries && entries[0];
                if (!entry) return;
                if (entry.isIntersecting || entry.intersectionRatio > 0) {
                    setShouldLoadCoverVideo(true);
                    try {
                        obs.disconnect();
                    } catch (_) {
                        /* noop */
                    }
                }
            },
            {
                threshold: 0.05,
                rootMargin: '200px 0px 200px 0px'
            }
        );

        obs.observe(el);
        return () => {
            cancelled = true;
            try {
                obs.disconnect();
            } catch (_) {
                /* noop */
            }
        };
    }, [coverVideoUrl]);

    return (
        <div
            ref={coverWrapRef}
            className={`relative w-full ${coverVideoUrl ? 'pt-[90%] sm:pt-[56.25%]' : 'pt-[120%] sm:pt-[33%]'}`}
        >
            <div className="absolute inset-0">
                {(desktopCoverUrl || mobileCoverUrl) ? (
                    <picture>
                        {mobileCoverUrl ? (
                            <source media="(max-width: 640px)" srcSet={getImageUrl(mobileCoverUrl)} />
                        ) : null}
                        <img
                            className="w-full h-full object-cover"
                            src={getImageUrl(desktopCoverUrl || mobileCoverUrl)}
                            alt="Обложка"
                            loading="eager"
                            decoding="async"
                        />
                    </picture>
                ) : (
                    <div className="w-full h-full hero-gradient from-primary-500/20 via-primary-400/15 to-secondary-500/20" />
                )}

                {coverVideoUrl && shouldLoadCoverVideo ? (
                    <>
                        <video
                            ref={coverVideoRef}
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${coverVideoReady ? 'opacity-100' : 'opacity-0'}`}
                            src={getImageUrl(coverVideoUrl)}
                            autoPlay
                            muted={coverMuted}
                            loop
                            playsInline
                            preload="none"
                            onLoadedData={() => setCoverVideoReady(true)}
                            onCanPlay={() => setCoverVideoReady(true)}
                        />

                        <div className="absolute top-4 right-4 z-20">
                            <div className="flex items-center gap-2 rounded-full bg-black/35 backdrop-blur-md border border-white/15 px-2 py-2 shadow-soft">
                                <button
                                    type="button"
                                    className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-primary-500 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/50"
                                    aria-label={coverMuted ? 'Включить звук' : 'Выключить звук'}
                                    title={coverMuted ? 'Включить звук' : 'Выключить звук'}
                                    onClick={async () => {
                                        const el = coverVideoRef.current;
                                        const nextMuted = !coverMuted;
                                        setCoverMuted(nextMuted);

                                        if (el && nextMuted === false) {
                                            try {
                                                await el.play();
                                            } catch (_) {
                                                setCoverMuted(true);
                                            }
                                        }
                                    }}
                                >
                                    {coverMuted ? (
                                        <VolumeX className="w-5 h-5 text-white" />
                                    ) : (
                                        <Volume2 className="w-5 h-5 text-white" />
                                    )}
                                </button>

                                {!coverMuted ? (
                                    <div className="flex items-center gap-2 pr-2">
                                        <span className="hidden sm:block text-[11px] text-white/80 select-none">Громкость</span>
                                        <input
                                            type="range"
                                            min={0}
                                            max={1}
                                            step={0.05}
                                            value={coverVolume}
                                            aria-label="Громкость"
                                            onChange={(e) => {
                                                const v = Number(e.target.value);
                                                if (!Number.isFinite(v)) return;
                                                setCoverVolume(v);
                                                if (v > 0 && coverMuted) setCoverMuted(false);
                                                if (v === 0) setCoverMuted(true);
                                            }}
                                            className="w-20 sm:w-28 accent-white"
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default CoverSection;
